import { ThisReceiver } from "@angular/compiler";
import { Component, Inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { Subscription } from "rxjs";
import { Gateway } from "src/components/shared-components/class/general-classes/gateway";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { ImportETSRoomNumberCalculationKind } from "src/model/enum/enumConnectionType";
import { SupportedFileTypeEnum } from "src/model/enum/enumFileHandling";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { AlertService } from "src/service/alert.service";
import { EventService } from "src/service/event.service";
import { GatewayKnxService } from "src/service/gateway-knx.service";
import { LoaderService } from "src/service/loader.service";
import { NetworkService } from "src/service/network.service";

@Component({
    selector: "app-popup-content-ets-import",
    templateUrl: "./popup-content-ets-import.component.html",
    styleUrls: ["./popup-content-ets-import.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupContentEtsImportComponent implements OnInit, OnDestroy {
  @Input("gateway") gateway: Gateway;

  public formEtsImportOptions: UntypedFormGroup = null;
  public textInsert: string = "";
  public showInputNRoomChar: boolean = true;

  private _networkServiceSubscription: Subscription = null;

  public ImportETSRoomNumberCalculationKind =
    ImportETSRoomNumberCalculationKind;
  public toolBarColor: string = "primary";
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public SupportedFileTypeEnum = SupportedFileTypeEnum;

  private _eventSubscriptions: Subscription[];

  public ETSProjFile: File = null;

  constructor(
    private _fb: UntypedFormBuilder,
    private _networkService: NetworkService,
    private _modalController: ModalController,
    private _gatewayKNXService: GatewayKnxService,
    private _eventService: EventService,
    private _alertService: AlertService,
    private _loaderService: LoaderService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.subscribeNetworkServiceData();
    this.subscribeEventService();
  }

  /**
   *
   */
  ngOnDestroy() {
    if (this._networkServiceSubscription) {
      this._networkServiceSubscription.unsubscribe();
    }
    for (let index = 0; index < this._eventSubscriptions.length; index++) {
      const element = this._eventSubscriptions[index];
      element.unsubscribe();
    }
  }

  /**
   * Handle go back button
   */
  public onClickCancel(): void {
    this._modalController.dismiss();
  }

  /**
   * Handle go ahead press button
   */
  public onClickImport(): void {
    let ncharforroomnumber = 0;
    if (
      this.formEtsImportOptions.value["roomNumberCalculationKind"] ==
      ImportETSRoomNumberCalculationKind.LastNChars
    ) {
      ncharforroomnumber = this.formEtsImportOptions.value["roomCharNumber"];
    }

    this._gatewayKNXService.ImportETSProj(
      this.ETSProjFile,
      this.gateway.instanceGUID,
      this.formEtsImportOptions.value["password"],
      ncharforroomnumber
    );
  }

  /**
   * Initialize form field
   */
  private initializeForm(): void {
    this.formEtsImportOptions = this._fb.group({
      password: new UntypedFormControl(""),
      roomNumberCalculationKind: new UntypedFormControl(
        ImportETSRoomNumberCalculationKind.LastSpace
      ),
      roomCharNumber: new UntypedFormControl(3, Validators.required),
    });
  }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  /**
   * Subscribe events
   */
  private subscribeEventService() {
    this._eventSubscriptions = [];
    this._eventSubscriptions.push(
      this._eventService.OnETSImportAbort.subscribe(async () => {
        await this._loaderService.dismissLoaderWaiting();
        this.handleApiResponseError();
        this._modalController.dismiss();
      })
    );
    this._eventSubscriptions.push(
      this._eventService.OnETSImportComplete.subscribe(async () => {
        await this._loaderService.dismissLoaderWaiting();
        this._modalController.dismiss("OK");
      })
    );
    this._eventSubscriptions.push(
      this._eventService.OnETSImportInProgressEvent.subscribe(
        (currentPercentageUploaded: number) => {
          this._loaderService.presentLoaderWaiting();
        }
      )
    );
  }

  /**
   * Allow the user to select the ETS proj file and save it
   */
  public onSelectETSProjectFile(files: FileList) {
    this.ETSProjFile = files.item(0);
  }

  /**
   * Show error message if something went wrong saving gateway settings or taking them
   */
  private async handleApiResponseError(): Promise<void> {
    this._alertService.presentAlertSomethingWrong();
  }
}
